<template>
  <div>
    <div v-if="!isMobileView">
      <ev-level-help />
      <directions-popup />
    </div>
    <corridor-help />
    <div id="afdc-station-background" v-on:click="closeStation" v-if="$route.path.includes('/station/')"></div>

    <div id="afdc-direction-background" v-if="directionsEnabled"></div>

    <div id="afdc-stations-map" :class="{'afdc-mobile-map-show':isMobileView}" tabindex="0"></div>
  </div>
</template>

<script>
import EvLevelHelp from '@/components/EvLevelHelp.vue';
import CorridorHelp from '@/components/CorridorHelp.vue';
import DirectionsPopup from '@/components/DirectionsPopup.vue';
import MapMixin from '@/lib/MapMixin';
import MapStationsLegend from '@/components/MapStationsLegend.vue';
import MapStationsPopup from '@/components/MapStationsPopup.vue';
import Vue from 'vue';
import mapboxgl from 'mapbox-gl/dist/mapbox-gl-unminified';
import nonreactive from '@/lib/nonreactive';
import turfCircle from '@turf/circle';

export default {
  name: 'MapStations',

  mixins: [
    MapMixin,
  ],

  components: {
    EvLevelHelp,
    DirectionsPopup,
    CorridorHelp,
  },

  beforeCreate() {
    this.mapContainer = document.createElement('div');
    this.mapContainer.style.height = '100%';
    this.mapContainer.style.width = '100%';
    this.map = new mapboxgl.Map({
      attributionControl: true,
      zoom: 3,
      minZoom: 1,
      maxZoom: 20,
      container: this.mapContainer,
    });

    this.map.addControl(new mapboxgl.NavigationControl({
      showZoom: !this.$store.getters.isMobileView,
      showCompass: false,
    }), 'bottom-right');

    this.legend = new Vue({
      ...MapStationsLegend,
      parent: this,
      propsData: {
        map: this.map,
      },
    });
    this.legend.$mount();
  },

  created() {
    this.fetchMapStyle();
  },

  computed: {
    countryBounds() {
      let southWest;
      let northEast;
      switch (this.$store.getters.getCountry) {
        case 'CA':
          southWest = new mapboxgl.LngLat(-141.5, 41.2);
          northEast = new mapboxgl.LngLat(-51.5, 67);
          break;
        case 'all':
          southWest = new mapboxgl.LngLat(-138, 24);
          northEast = new mapboxgl.LngLat(-51.5, 58);
          break;
        default:
          southWest = new mapboxgl.LngLat(-126, 24);
          northEast = new mapboxgl.LngLat(-66, 49.5);
          break;
      }
      return new mapboxgl.LngLatBounds(southWest, northEast);
    },

    queryFuel() {
      return this.$store.getters.getFilterValueWithDefault('fuel');
    },

    stationPointsGeojson() {
      if (this.$store.getters.isNavActiveFindModeNearest) {
        return this.$store.getters.stationPointsGeojson;
      }
      if (this.$store.getters.isNavActiveFindModeRoute) {
        return this.$store.getters.routeStationsGeojson;
      }
      if (this.$store.getters.isNavActiveTabCorridors) {
        return this.$store.getters.stationPointsGeojson;
      }
      if (this.$store.getters.isNavActiveTabAnalyze) {
        return this.$store.getters.analyzeStationsGeojson;
      }
      return null;
    },

    searchLocationsGeojson() {
      if (this.$store.getters.isNavActiveFindModeNearest) {
        return this.$store.getters.nearestStationsSearchLocationsGeojson;
      }
      if (this.$store.getters.isNavActiveFindModeRoute) {
        return this.$store.getters.routeStationsSearchLocationsGeojson;
      }
      if (this.$store.getters.isNavActiveTabAnalyze) {
        return this.$store.getters.analyzeStationsSearchLocationsGeojson;
      }
      return null;
    },

    corridorStartStopGeojson() {
      // this is being done here instead of the store because the pins need to be added whenever a new point is started or ended.
      // adding it just when the api was called did not give it the immediacy it needed
      const start = this.$store.getters.getCorridorRouteStart;
      const end = this.$store.getters.getCorridorRouteEnd;
      const geojson = {
        type: 'FeatureCollection',
        features: [],
      };
      if (start.latitude) {
        geojson.features.push({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [start.longitude, start.latitude],
          },
          properties: {
            color: 'green',
            routePoint: 'start',
          },
        });
      }
      if (end.latitude) {
        geojson.features.push({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [end.longitude, end.latitude],
          },
          properties: {
            color: 'red',
            routePoint: 'end',
          },
        });
      }
      return geojson;
    },

    corridorRouteMode() {
      return this.$store.getters.getFilterValueWithDefault('show_corridor_routes');
    },

    corridorStationMode() {
      return this.$store.getters.getFilterValueWithDefault('show_corridor_stations');
    },

    corridorStateMode() {
      return this.$store.getters.getFilterValueWithDefault('state');
    },

    findAllStationsGeojson() {
      return this.$store.getters.stationPointsGeojson;
    },

    findNearestStationsGeojson() {
      return this.$store.getters.nearestStationsGeojson;
    },

    findRouteStationsGeojson() {
      return this.$store.getters.routeStationsGeojson;
    },

    findRouteStationsLineGeojson() {
      return this.$store.getters.routeStationsLineGeojson;
    },

    npsStationsGeojson() {
      return this.$store.getters.getNpsBoundaries;
    },

    findSelectedPark() {
      return this.$store.getters.getSelectedPark;
    },

    findCorridorStationsLineGeojson() {
      return this.$store.getters.corridorStationsLineGeojson;
    },

    findCorridorState() {
      return this.$store.getters.getCorridorState;
    },

    findCorridorRouteIndex() {
      return this.$store.getters.getCorridorRouteIndex;
    },

    findRouteStationsInfo() {
      return this.$store.getters.routeStationsInfo;
    },

    analyzeStationsGeojson() {
      return this.$store.getters.analyzeStationsGeojson;
    },

    directionsEnabled() {
      return this.$store.getters.getDirectionPopup;
    },

    analyzeNearestRadiusCircleGeojson() {
      const center = this.$store.getters.analyzeNearestCenter;
      if (center) {
        const radiusMiles = parseInt(this.$store.getters.getFilterValueWithDefault('radius'), 10);
        const units = (this.$store.getters.getFilterValueWithDefault('radius_unit') === 'km') ? 'kilometers' : 'miles';
        const circle = turfCircle(center, radiusMiles, {
          units,
          steps: 128,
        });
        return nonreactive.frozen({
          type: 'FeatureCollection',
          features: [circle],
        });
      }
      return null;
    },
    isMobileView() {
      return this.$store.getters.isMobileView;
    },
  },

  watch: {
    '$store.getters.getCountry': 'zoomMap',

    '$store.getters.getResetMapZoom': 'resetMapZoom',

    '$store.getters.isMobileMapResize': 'updateSize',

    '$store.getters.getResultsHover': {
      immediate: true,
      handler: 'resizeFeatureByType',
    },

    '$store.getters.getFilters': {
      immediate: true,
      handler: 'fetchData',
    },

    // handle layers when the view changes
    '$store.getters.getNavActiveMapMode': {
      immediate: true,
      handler: 'handleViewChange',
    },

    // Resize the map whenever the left-column results hides/shows.
    '$store.getters.hasNavStationResults': 'updateSize',

    stationPointsGeojson: {
      immediate: true,
      handler: 'drawStationPoints',
    },

    findNearestStationsGeojson: {
      immediate: true,
      handler: 'drawFindNearest',
    },

    findRouteStationsGeojson: {
      immediate: true,
      handler: 'drawFindRoute',
    },

    findCorridorStationsLineGeojson: {
      immediate: true,
      handler: 'drawFindCorridor',
    },

    npsStationsGeojson: {
      immediate: true,
      handler: 'drawParkBoundaries',
    },

    findCorridorRouteIndex: {
      immediate: true,
      handler: 'highlightCorridorRoute',
    },

    findSelectedPark: {
      immediate: true,
      handler: 'zoomToPark',
    },

    corridorStateMode: 'zoomToCorridorState',

    '$store.getters.isNavActiveTabCorridorsSearchPresent': {
      immediate: true,
      handler: 'drawFindCorridor',
    },

    '$store.getters.getCorridorRouteStart': {
      immedidate: true,
      handler: 'drawCorridorStartStop',
    },

    '$store.getters.getCorridorRouteEnd': {
      immedidate: true,
      handler: 'drawCorridorStartStop',
    },

    '$store.getters.getLoadingCorridorStations': {
      immediate: true,
      handler: 'clearCorridorWhileLoading',
    },

    corridorStationMode: 'toggleCorridorStations',

    '$store.getters.getNpsBoundaryCheck': {
      immediate: true,
      handler: 'toggleParkStations',
    },

    corridorRouteMode: {
      immediate: true,
      handler: 'toggleCorridorShapefile',
    },

    searchLocationsGeojson: {
      immediate: true,
      handler: 'drawSearchLocations',
    },

    analyzeNearestRadiusCircleGeojson: {
      immediate: true,
      handler: 'drawAnalyze',
    },

    queryFuel: {
      immediate: true,
      handler: 'toggleLegend',
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.initMap();
    });
  },

  beforeDestroy() {
    if (this.map) {
      this.map.remove();
    }
  },

  methods: {
    addImageDataToMap() {
      const pixelRatio = this.$store.getters.mapPixelRatio;
      const imageData = this.$store.getters.mapImageData;
      Object.keys(imageData).forEach((pinId) => {
        this.map.addImage(pinId, imageData[pinId], { pixelRatio });
      });
    },

    setupStationPointsLayer() {
      // Add a layer for the station points, regardless of the source (so this
      // could be the base map of all stations, or a route results, or the
      // analyze results).
      this.map.addSource('station-points-source', {
        type: 'geojson',
        data: this.stationPointsGeojson,
      });
      this.stationPointsSource = this.map.getSource('station-points-source');
      const radiusCircle = this.$store.getters.isMobileView ? [[11, 7], [20, 10]] : [[11, 5.5], [20, 10]];

      const paint = {
        'circle-radius': {
          stops: radiusCircle,
        },
        'circle-stroke-width': 1,
        'circle-stroke-color': '#7c7b7b',
        'circle-color': [
          'match',
          ['get', 'fuel'],
          'BD', '#b53352',
          'CNG', '#265bb2',
          'E85', '#ccc876',
          'ELEC', '#2db262',
          'HY', '#a2d5e0',
          'LNG', '#134427',
          'LPG', '#9b8bcc',
          'RD', '#f09e91',
          /* else */ '#000000',
        ],
      };

      this.map.addLayer({
        id: 'station-points-layer',
        type: 'circle',
        source: 'station-points-source',
        paint,
      });
      this.stationPointsLayer = this.map.getLayer('station-points-layer');

      // Add a second layer of just the route points (so the dataset is
      // smaller) used for displaying larger points on hover.
      this.map.addSource('station-route-points-source', {
        type: 'geojson',
        data: this.findRouteStationsGeojson,
      });
      this.stationRoutePointsSource = this.map.getSource('station-route-points-source');

      this.map.addLayer({
        id: 'station-route-points-hover-layer',
        type: 'circle',
        source: 'station-route-points-source',
        paint: {
          ...paint,
          'circle-radius': {
            stops: [
              [11, 11],
              [20, 20],
            ],
          },
        },
        filter: ['==', 'id', ''],
      });
      this.stationRoutePointsHoverLayer = this.map.getLayer('station-route-points-hover-layer');
    },

    setupNpsBoundaryLayer() {
      this.map.addSource('park-boundary-source', {
        type: 'geojson',
        data: this.npsStationsGeojson,
      });

      this.parkSource = this.map.getSource('park-boundary-source');

      this.map.addLayer({
        id: 'park-boundary-layer',
        type: 'fill',
        source: 'park-boundary-source',
        paint: {
          'fill-color': '#D1C6B8',
          'fill-opacity': 0.9,
        },
      });

      this.map.addLayer({
        id: 'park-label-layer',
        type: 'symbol',
        source: 'park-boundary-source',
        minzoom: 6,
        layout: {
          'text-field': ['get', 'unit_name'],
          'text-size': 10,
          'text-variable-anchor': ['top', 'bottom', 'left'],
        },
      });
    },

    setupCorridorStartStopLayer() {
      this.map.addSource('corridor-start-stop-marker-source', {
        type: 'geojson',
        data: this.corridorStartStopGeojson,
      });
      this.corridorStartStopSource = this.map.getSource('corridor-start-stop-marker-source');

      this.map.addLayer({
        id: 'corridor-start-stop-marker-layer',
        type: 'symbol',
        source: 'corridor-start-stop-marker-source',
        layout: {
          'icon-image': 'pin-search-location-{color}',
          'icon-size': 1.0,
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-anchor': 'bottom',
        },
      });
      this.corridorStartStopMarkerLayer = this.map.getLayer('corridor-start-stop-marker-layer');
    },
    setupSearchLocationMarkersLayer() {
      this.map.addSource('search-locations-markers-source', {
        type: 'geojson',
        data: this.searchLocationsGeojson,
      });
      this.searchLocationMarkersSource = this.map.getSource('search-locations-markers-source');

      this.map.addLayer({
        id: 'search-locations-markers-layer',
        type: 'symbol',
        source: 'search-locations-markers-source',
        layout: {
          'icon-image': 'pin-search-location-{color}',
          'icon-size': 1.0,
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-anchor': 'bottom',
        },
      });
      this.searchLocationMarkersLayer = this.map.getLayer('search-locations-markers-layer');
    },

    setupStationNearestMarkersLayer() {
      this.map.addSource('station-nearest-markers-source', {
        type: 'geojson',
        data: this.findNearestStationsGeojson,
      });
      this.stationNearestMarkersSource = this.map.getSource('station-nearest-markers-source');

      this.map.addLayer({
        id: 'station-nearest-markers-layer',
        type: 'symbol',
        source: 'station-nearest-markers-source',
        layout: {
          'icon-image': 'pin-{fuel}-{index}',
          'icon-size': 1.0,
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-anchor': 'bottom',
        },
      });
      this.stationNearestMarkersLayer = this.map.getLayer('station-nearest-markers-layer');

      this.map.addLayer({
        id: 'station-nearest-markers-hover-layer',
        type: 'symbol',
        source: 'station-nearest-markers-source',
        layout: {
          'icon-image': 'pin-hover-{fuel}-{index}',
          'icon-size': 1.0,
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-anchor': 'bottom',
        },
        filter: ['==', 'id', ''],
      });
      this.stationNearestMarkersHoverLayer = this.map.getLayer('station-nearest-markers-hover-layer');
    },

    setupFindRouteLineLayer() {
      this.map.addSource('find-route-line-source', {
        type: 'geojson',
        data: this.findRouteStationsLineGeojson,
      });
      this.findRouteLineSource = this.map.getSource('find-route-line-source');

      this.map.addLayer({
        id: 'find-route-line-layer',
        type: 'line',
        source: 'find-route-line-source',
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': '#0159AA',
          'line-width': {
            stops: [
              [11, 4],
              [20, 12],
            ],
          },
        },
      });
      this.findRouteLineLayer = this.map.getLayer('find-route-line-layer');
    },

    setupCorridorRouteLineLayer() {
      this.map.addSource('corridor-route-line-source', {
        type: 'geojson',
        data: this.findCorridorStationsLineGeojson,
      });
      this.findCorridorLineSource = this.map.getSource('corridor-route-line-source');
      this.map.addLayer({
        id: 'find-corridor-line-layer',
        type: 'line',
        source: 'corridor-route-line-source',
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
      });
      this.findCorridorLineLayer = this.map.getLayer('find-corridor-line-layer');
      this.highlightCorridorRoute();
    },

    setupCorridorShapefileLayer() {
      this.map.addSource('fuel-corridor', {
        type: 'vector',
        tiles: [
          'https://widgets.nrel.gov/afdc/map-tiles/alternative-fuel-corridors-round-7/{z}/{x}/{y}.pbf',
        ],
        minzoom: 0,
        maxzoom: 14,
      });
      this.corridorShapefileSource = this.map.getSource('fuel-corridor');
      this.map.addLayer({
        id: 'find-fuel-corridor',
        type: 'line',
        source: 'fuel-corridor',
        'source-layer': 'alternative_fuel_corridors',
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
      });
      this.styleCorridorRoute();
    },

    setupAnalyzeLayer() {
      this.map.addSource('analyze-source', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [],
        },
      });
      this.analyzeSource = this.map.getSource('analyze-source');
    },

    setupAnalyzeRadiusCircleLayer() {
      this.map.addSource('analyze-radius-circle-source', {
        type: 'geojson',
        data: this.analyzeNearestRadiusCircleGeojson,
      });
      this.analyzeRadiusCircleSource = this.map.getSource('analyze-radius-circle-source');

      this.map.addLayer({
        id: 'analyze-radius-circle-fill-layer',
        type: 'fill',
        source: 'analyze-radius-circle-source',
        paint: {
          'fill-color': '#ffffff',
          'fill-opacity': 0.35,
        },
      });
      this.analyzeRadiusCircleFillLayer = this.map.getLayer('analyze-radius-circle-fill-layer');

      this.map.addLayer({
        id: 'analyze-radius-circle-layer',
        type: 'line',
        source: 'analyze-radius-circle-source',
        paint: {
          'line-color': '#399af7',
          'line-opacity': 0.9,
          'line-width': {
            stops: [
              [11, 3],
              [20, 6],
            ],
          },
        },
      });
      this.analyzeRadiusCircleLayer = this.map.getLayer('analyze-radius-circle-layer');
    },

    setupCountryBoundsLayerDebug() {
      const geojson = [[
        this.countryBounds.getSouthWest().toArray(),
        this.countryBounds.getSouthEast().toArray(),
        this.countryBounds.getNorthEast().toArray(),
        this.countryBounds.getNorthWest().toArray(),
      ]];
      this.map.addLayer({
        id: 'country-bounds-layer',
        type: 'fill',
        source: {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: {
              type: 'Polygon',
              coordinates: geojson,
            },
          },
        },
        layout: {},
        paint: {
          'fill-color': '#088',
          'fill-opacity': 0.6,
        },
      });
    },

    addDataToMap() {
      this.addImageDataToMap();
      this.setupAnalyzeRadiusCircleLayer();
      this.setupCorridorShapefileLayer();
      if (this.$store.getters.isNpsFunded) {
        this.setupNpsBoundaryLayer();
      }
      this.setupStationPointsLayer();
      this.setupSearchLocationMarkersLayer();
      this.setupStationNearestMarkersLayer();
      this.setupFindRouteLineLayer();
      this.setupCorridorRouteLineLayer();
      this.setupCorridorStartStopLayer();
      this.setupAnalyzeLayer();

      // Only enable this to view the country bounds for debugging/adjusting
      // the country bounds.
      // this.setupCountryBoundsLayerDebug();

      this.map.on('mousemove', 'station-points-layer', () => {
        this.map.getCanvas().style.cursor = 'pointer';
      });
      this.map.on('mouseleave', 'station-points-layer', () => {
        this.map.getCanvas().style.cursor = '';
      });

      this.map.on('mousemove', 'station-nearest-markers-layer', () => {
        this.map.getCanvas().style.cursor = 'pointer';
      });
      this.map.on('mouseleave', 'station-nearest-markers-layer', () => {
        this.map.getCanvas().style.cursor = '';
      });
      this.map.on('click', 'station-points-layer', this.clickStationFeature.bind(this));
      this.map.on('click', 'station-nearest-markers-layer', this.clickStationFeature.bind(this));
      this.map.on('click', 'find-corridor-line-layer', this.setAlternateRoute.bind(this));

      let hoverStateId = null;

      this.map.on('mousemove', 'find-corridor-line-layer', (event) => {
        const currentRoute = this.findCorridorRouteIndex;
        // makes alternate route thicker on hover
        if (event.features.length > 0) {
          if (event.features[0].id !== currentRoute) {
            this.map.setFeatureState(
              { source: 'corridor-route-line-source', id: event.features[0].id },
              { hover: true },
            );
            this.map.getCanvas().style.cursor = 'pointer';
            hoverStateId = event.features[0].id;
          }
        }
      });

      this.map.on('mouseleave', 'find-corridor-line-layer', () => {
        this.map.getCanvas().style.cursor = '';
        this.map.setFeatureState(
          { source: 'corridor-route-line-source', id: hoverStateId },
          { hover: false },
        );
        hoverStateId = null;
      });

      this.mapDataLoaded = true;
      this.handleViewChange();
      this.zoomMap();
    },

    clickStationFeature(e) {
      if (this.popup) {
        this.popup.$destroy();
      }

      this.popup = new Vue({
        ...MapStationsPopup,
        parent: this,
        propsData: {
          map: this.map,
          feature: e.features[0],
          clickPoint: e.lngLat,
        },
      });
      this.popup.$mount();
    },

    setAlternateRoute(e) {
      const newRouteIndex = e.features[0].properties.layerIndex;
      this.$store.commit('setCorridorIndex', newRouteIndex);
    },

    closeStation() {
      this.$router.push({
        name: this.$store.getters.getNavActiveFilterRouteName,
        query: this.$store.getters.getFilters,
      });
    },

    initMap() {
      document.getElementById('afdc-stations-map').appendChild(this.mapContainer);
      if (this.$store.getters.isMobileView) {
        document.querySelector('.afdc-mobile-map-show').style.height = `${window.innerHeight - 100}px`;
      }
      this.map.resize();
      // Trigger a default zoom to the default country bounds as soon as we
      // know the size of the map container (but probably before data has
      // loaded). Performing this default country zoom early on prevents a
      // subtle double rendering and shift in the map (at least of the default
      // initial country-wide map) if we waited to do this until the data had
      // loaded.
      this.zoomMap();
    },

    zoomMap() {
      // Build up the boundaries of what data we need to zoom to fit based on
      // the bounds of the rendered layers.
      let bounds = new mapboxgl.LngLatBounds();

      if (this.mapDataLoaded) {
        // Include the search location pin (or starting/ending pins for routing
        // results) in the bounds.
        if (this.map.getLayoutProperty('search-locations-markers-layer', 'visibility') === 'visible') {
          const geojson = this.searchLocationsGeojson;
          if (geojson && geojson.features) {
            for (let i = 0, len = geojson.features.length; i < len; i += 1) {
              const feature = geojson.features[i];
              bounds = bounds.extend(feature.geometry.coordinates);
            }
          }
        }

        // Include the station marker icons for "nearest" searches in the
        // bounds.
        if (this.map.getLayoutProperty('station-nearest-markers-layer', 'visibility') === 'visible') {
          const geojson = this.findNearestStationsGeojson;
          if (geojson && geojson.features) {
            for (let i = 0, len = geojson.features.length; i < len; i += 1) {
              const feature = geojson.features[i];
              bounds = bounds.extend(feature.geometry.coordinates);
            }
          }
        }

        // For route results, include the route line in the bounds.
        const findRouteLineLayerVisibility = this.map.getLayoutProperty('find-route-line-layer', 'visibility');
        const findCorridorLineLayerVisibility = this.map.getLayoutProperty('find-corridor-line-layer', 'visibility');
        if (findRouteLineLayerVisibility === 'visible' || findCorridorLineLayerVisibility === 'visible') {
          const lineGeojson = this.$store.getters.isNavActiveTabCorridors ? this.findCorridorStationsLineGeojson : this.findRouteStationsLineGeojson;
          if (lineGeojson && lineGeojson.features) {
            for (let i = 0, len = lineGeojson.features.length; i < len; i += 1) {
              const feature = lineGeojson.features[i];
              for (let j = 0, coordLen = feature.geometry.coordinates.length; j < coordLen; j += 1) {
                const coordinates = feature.geometry.coordinates[j];
                bounds = bounds.extend(coordinates);
              }
            }
          }
        }

        // On the analyze tab, include the radius circle (when performing a
        // search around a location) in the bounds.
        if (this.map.getLayoutProperty('analyze-radius-circle-layer', 'visibility') === 'visible') {
          const geojson = this.analyzeNearestRadiusCircleGeojson;
          if (geojson && geojson.features && geojson.features[0]) {
            const feature = geojson.features[0];
            for (let i = 0, len = feature.geometry.coordinates[0].length; i < len; i += 1) {
              const coordinates = feature.geometry.coordinates[0][i];
              bounds = bounds.extend(coordinates);
            }
          }
        }

        // For routing results or analyze state-based queries, include all the
        // individual station points along the route in the bounds.
        //
        // TODO: This could eventually become a performance bottleneck if there
        // are lots of station points in the results (particularly for the
        // analyze state results, which might include thousands of points). So
        // for both situations, it might be better to have the API respond with
        // a simple bounding box of the results which we could zoom to here,
        // since it would probably be quicker to calculate that bounding box
        // server-side, rather than client-side.
        if (findRouteLineLayerVisibility === 'visible' || (this.$store.getters.isNavActiveTabAnalyze && this.$store.getters.getFilterValueWithDefault('region') !== 'all')) {
          // Include all the individual station points along the route in the
          // bounds.
          const geojson = this.stationPointsGeojson;
          if (geojson && geojson.features) {
            for (let i = 0, len = geojson.features.length; i < len; i += 1) {
              const feature = geojson.features[i];
              bounds = bounds.extend(feature.geometry.coordinates);
            }
          }
        }
      }

      const basePadding = 10;

      // Margin to right of controls.
      let controlWidth = 10;
      if (this.legend && this.legend.added) {
        // Width of legend control.
        controlWidth += 100;
      } else {
        // Width of zoom control.
        controlWidth += 30;
      }

      // this removes legend padding on mobile map
      if (this.$store.getters.isMobileView) {
        controlWidth = 10;
      }

      let markerHeightPadding = this.$store.getters.mapMaxMarkerHeight;
      // Only half of the marker's width needs to be accounted for padding
      // purposes (since the other half will be in the opposite direction,
      // falling inside the map).
      let markerWidthPadding = this.$store.getters.mapMaxMarkerWidth / 2;

      // Allow more bottom padding to account for the attribution control.
      const bottomPadding = 20;
      let topPadding = 0;

      // If none of the zooming behavior applies to the current query, then
      // zoom to the default country extent.
      if (bounds.isEmpty()) {
        bounds = this.countryBounds;

        // When zooming to the country, don't account for marker sizing, since
        // there are no large markers on this view, and this allows our country
        // bounds to be more easily customized to a tighter view.
        markerHeightPadding = 0;
        markerWidthPadding = 0;

        // Assign equal padding to the top as to the bottom, just to better
        // center the country bounds.
        topPadding = bottomPadding;
      }

      if (this.map) {
        this.map.fitBounds(bounds, {
          animate: false,
          padding: {
            top: topPadding + markerHeightPadding + basePadding,
            bottom: bottomPadding + basePadding,
            left: markerWidthPadding + basePadding,
            right: markerWidthPadding + controlWidth + basePadding,
          },
        });

        // If there weren't any results for the find nearest mode, then zoom
        // out to provide context.
        //
        // In the case of 0 results, zooming to fit the extent has zooomed to
        // fit the search location pin, which means you'll be zoomed in way too
        // far. We'll still zoom to fit (to handle panning/centering to the
        // proper location), but in this case, then zoom out, so you have
        // better context about what city was matched, so you know if the
        // search location was geocoded correctly or you're in the wrong
        // location.
        if (this.$store.getters.isNavActiveFindModeNearestSearchPresent
          && this.$store.getters.nearestStationsTotal === 0) {
          this.map.setZoom(8);
        }
      }
    },

    handleViewChange() {
      if (this.mapDataLoaded) {
        const layerVisibility = {
          'search-locations-markers-layer': 'none',
          'station-points-layer': 'none',
          'station-route-points-hover-layer': 'none',
          'station-nearest-markers-layer': 'none',
          'station-nearest-markers-hover-layer': 'none',
          'find-route-line-layer': 'none',
          'analyze-radius-circle-layer': 'none',
          'analyze-radius-circle-fill-layer': 'none',
          'find-corridor-line-layer': 'none',
          'corridor-start-stop-marker-layer': 'none',
          'find-fuel-corridor': 'none',
        };
        const showParks = this.$store.getters.isNpsFunded && this.$store.getters.getNpsBoundaryCheck;

        if (showParks) {
          layerVisibility['park-boundary-layer'] = 'none';
          layerVisibility['park-label-layer'] = 'none';
        }

        switch (this.$store.getters.getNavActiveMapMode) {
          case 'find-nearest':
            // show layers for find nearest stations mode before a search
            layerVisibility['station-points-layer'] = 'visible';
            if (showParks) {
              layerVisibility['park-boundary-layer'] = 'visible';
              layerVisibility['park-label-layer'] = 'visible';
            }
            break;
          case 'find-nearest-results':
            // show layers for find nearest stations
            layerVisibility['station-points-layer'] = 'visible';
            layerVisibility['station-nearest-markers-layer'] = 'visible';
            layerVisibility['station-nearest-markers-hover-layer'] = 'visible';
            layerVisibility['search-locations-markers-layer'] = 'visible';
            if (showParks) {
              layerVisibility['park-boundary-layer'] = 'visible';
              layerVisibility['park-label-layer'] = 'visible';
            }
            break;
          case 'find-route':
            // show layers for find route mode before a search
            // No layers shown
            break;
          case 'find-route-results':
            // show layers for find route
            layerVisibility['station-points-layer'] = 'visible';
            layerVisibility['station-route-points-hover-layer'] = 'visible';
            layerVisibility['find-route-line-layer'] = 'visible';
            layerVisibility['search-locations-markers-layer'] = 'visible';
            break;
          case 'analyze':
            // show layers for analyze
            layerVisibility['station-points-layer'] = 'visible';
            layerVisibility['analyze-radius-circle-layer'] = 'visible';
            layerVisibility['analyze-radius-circle-fill-layer'] = 'visible';
            layerVisibility['search-locations-markers-layer'] = 'visible';
            break;
          case 'corridors':
            // show layers for find nearest stations mode before a search
            layerVisibility['corridor-start-stop-marker-layer'] = 'visible';
            break;
          default:
            break;
        }

        Object.keys(layerVisibility).forEach((layerId) => {
          this.map.setLayoutProperty(layerId, 'visibility', layerVisibility[layerId]);
        });

        if (this.$store.getters.isNavActiveTabCorridors) {
          this.toggleCorridorStations();
          this.toggleCorridorShapefile();
        }
      }
    },

    resetMapZoom() {
      if (this.$store.getters.getResetMapZoom) {
        this.$nextTick(() => {
          const mapExtent = this.countryBounds;
          const options = { constrainResolution: false };
          this.map.getView().fit(mapExtent, options);
        });
      }

      this.$store.commit('setResetMapZoom', false);
    },

    resizeFeatureByType() {
      if (this.map && this.mapDataLoaded) {
        if (this.$store.getters.getResultsHover) {
          this.map.setFilter('station-nearest-markers-hover-layer', ['==', 'id', this.$store.getters.getResultsHover]);
          this.map.setFilter('station-route-points-hover-layer', ['==', 'id', this.$store.getters.getResultsHover]);
        } else {
          this.map.setFilter('station-nearest-markers-hover-layer', ['==', 'id', '']);
          this.map.setFilter('station-route-points-hover-layer', ['==', 'id', '']);
        }
      }
    },

    fetchData() {
      if (this.$store.getters.isNavActiveFindModeNearest || this.$store.getters.isNavActiveTabCorridors) {
        this.$store.dispatch('fetchStationPoints', this.$store.getters.getFiltersWithDefaults);
      }
      if (this.$store.getters.getNpsBoundaryCheck && this.$store.getters.isNpsFunded) {
        this.$store.dispatch('fetchParkBoundaries', this.$store.getters.getFiltersWithDefaults);
      }
      if (this.$store.getters.isNavActiveTabCorridors) {
        this.styleCorridorRoute();
      }
    },

    drawStationPoints() {
      if (this.stationPointsSource) {
        this.stationPointsSource.setData(this.stationPointsGeojson);
      }
    },

    drawParkBoundaries() {
      const npsGeo = this.npsStationsGeojson.features;
      if (this.$store.getters.isNpsFunded && this.parkSource) {
        if (npsGeo.length) {
          this.parkSource.setData(this.npsStationsGeojson);
        }
      }
    },

    drawCorridorStartStop() {
      if (this.corridorStartStopSource) {
        this.corridorStartStopSource.setData(this.corridorStartStopGeojson);
      }
    },

    toggleCorridorShapefile() {
      const showCorridors = this.corridorRouteMode ? 'visible' : 'none';
      // const showCorridors = this.$store.getters.getShowCorridorShapefile ? 'visible' : 'none';
      if (this.map.style) {
        if (this.$store.getters.isNavActiveTabCorridors) {
          this.map.setLayoutProperty('find-fuel-corridor', 'visibility', showCorridors);
          this.toggleLegend();
        } else {
          this.map.setLayoutProperty('find-fuel-corridor', 'visibility', 'none');
        }
      }
    },

    toggleParkStations() {
      const showParks = this.$store.getters.isNpsFunded && this.$store.getters.getNpsBoundaryCheck;
      const parkVisibility = showParks ? 'visible' : 'none';
      if (this.map.style) {
        if (showParks) {
          this.map.setLayoutProperty('park-boundary-layer', 'visibility', parkVisibility);
          this.map.setLayoutProperty('park-label-layer', 'visibility', parkVisibility);
        } else {
          this.map.setLayoutProperty('park-boundary-layer', 'visibility', 'none');
          this.map.setLayoutProperty('park-label-layer', 'visibility', 'none');
        }
      }
    },

    toggleCorridorStations() {
      this.updateSize();
      const showStations = this.$store.getters.getFilterValueWithDefault('show_corridor_stations') === true ? 'visible' : 'none';
      if (this.map.style) {
        if (this.$store.getters.isNavActiveTabCorridors) {
          this.map.setLayoutProperty('station-points-layer', 'visibility', showStations);
          this.drawFindCorridor();
          this.map.setLayoutProperty('corridor-start-stop-marker-layer', 'visibility', showStations);
        } else {
          this.map.setLayoutProperty('station-points-layer', 'visibility', 'visible');
          this.drawFindCorridor();
        }
      }
    },
    zoomToPark() {
      let parkBounds;
      const parkBoundaries = this.findSelectedPark.features[0] || null;
      if (parkBoundaries && (typeof parkBoundaries.geometry !== 'undefined')) {
        const polygonType = parkBoundaries.geometry.type;
        const coordinates = parkBoundaries.geometry.coordinates[0] || null;
        if (polygonType === 'Polygon') {
          parkBounds = coordinates.reduce((bounds, coord) => bounds.extend(coord), new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]));
          // this is for multipolygon park which have a different geojson data structure
        } else {
          const multiCoordinates = parkBoundaries.geometry.coordinates;
          const multiCoordinateArray = [];
          multiCoordinates.forEach((set) => {
            set[0].forEach((coordinate) => {
              multiCoordinateArray.push(coordinate);
            });
          });
          parkBounds = multiCoordinateArray.reduce((bounds, coord) => bounds.extend(coord), new mapboxgl.LngLatBounds(multiCoordinateArray[0], multiCoordinateArray[0]));
        }
      }
      if (this.map && parkBounds) {
        this.map.fitBounds(parkBounds, {
          padding: 20,
        });
      } else {
        this.zoomMap();
      }
    },
    zoomToCorridorState() {
      const hasStartStop = this.$route.query.start !== undefined || this.$route.query.end !== undefined;
      const stateBounds = this.findCorridorState;
      if (this.map && stateBounds.length) {
        this.map.fitBounds(stateBounds, {
          padding: 20,
        });
        if (hasStartStop) {
          this.$store.commit('setCorridorRouteStart', {});
          this.$store.commit('setCorridorRouteEnd', {});
        }
      } else if (!hasStartStop) {
        this.zoomMap();
      }
    },
    drawFindNearest() {
      if (this.$store.getters.isNavActiveFindModeNearest) {
        if (this.stationNearestMarkersSource) {
          this.stationNearestMarkersSource.setData(this.findNearestStationsGeojson);
        }

        this.drawSearchLocations();
        this.zoomMap();
      }
    },

    drawFindCorridor() {
      const lineCoordinates = this.findCorridorStationsLineGeojson.features;
      if (this.findCorridorLineSource) {
        if (lineCoordinates.length && this.$store.getters.isNavActiveTabCorridorsSearchPresent && !this.$store.getters.getLoadingCorridorStations && this.corridorStationMode) {
          this.findCorridorLineSource.setData(this.findCorridorStationsLineGeojson);
          this.map.setLayoutProperty('find-corridor-line-layer', 'visibility', 'visible');
          this.zoomMap();
        } else {
          this.map.setLayoutProperty('find-corridor-line-layer', 'visibility', 'none');
        }
      }
    },

    clearCorridorWhileLoading() {
      if (this.$store.getters.getLoadingCorridorStations && this.$store.getters.isNavActiveTabCorridors && this.map.style) {
        this.map.setLayoutProperty('find-corridor-line-layer', 'visibility', 'none');
      }
    },

    highlightCorridorRoute() {
      if (this.map.style && (this.findCorridorRouteIndex || this.findCorridorRouteIndex === 0)) {
        const currentRoute = this.findCorridorRouteIndex;
        const corridorLineLayer = this.map.getLayer('find-corridor-line-layer');
        if (corridorLineLayer) {
          this.map.setPaintProperty('find-corridor-line-layer', 'line-color', ['case', ['==', currentRoute, ['get', 'layerIndex']], '#000', '#969696']);
          this.map.setPaintProperty('find-corridor-line-layer', 'line-width', ['case', ['boolean', ['feature-state', 'hover'], false], 4, 3]);
          this.map.setPaintProperty('find-corridor-line-layer', 'line-offset', { property: 'layerIndex', type: 'categorical', stops: [[0, -3.5], [1, 0], [2, 3.5]] });
        }
      }
    },

    styleCorridorRoute() {
      const fuel = this.queryFuel[0];
      const fuelColors = this.$store.getters.getCorridorColors;

      if (this.map.style && this.$store.getters.isNavActiveTabCorridors) {
        const readyColor = fuelColors[fuel].ready;
        const pendingColor = fuelColors[fuel].pending;
        this.map.setFilter('find-fuel-corridor', ['>=', ['get', fuel], 1]);
        this.map.setPaintProperty('find-fuel-corridor', 'line-color', ['match', ['get', fuel], 1, readyColor, 2, pendingColor, '#ccc']);
        this.map.setPaintProperty('find-fuel-corridor', 'line-width', 3);
      }
    },

    drawFindRoute() {
      if (this.$store.getters.isNavActiveFindModeRoute) {
        if (this.stationRoutePointsSource) {
          this.stationRoutePointsSource.setData(this.findRouteStationsGeojson);
        }
        if (this.findRouteLineSource) {
          this.findRouteLineSource.setData(this.findRouteStationsLineGeojson);
        }
        this.drawSearchLocations();
        this.zoomMap();
      }
    },

    drawAnalyze() {
      if (this.$store.getters.isNavActiveTabAnalyze) {
        if (this.analyzeRadiusCircleSource) {
          this.analyzeRadiusCircleSource.setData(this.analyzeNearestRadiusCircleGeojson);
        }

        this.drawSearchLocations();
        this.zoomMap();
      }
    },

    drawSearchLocations() {
      if (this.searchLocationMarkersSource) {
        this.searchLocationMarkersSource.setData(this.searchLocationsGeojson);
      }
    },

    updateSize() {
      const stateBoundaries = this.findCorridorState || null;
      const parkBoundaries = this.findSelectedPark.features[0] || null;
      // Re-calculate the map size, but do it on the next tick to ensure any
      // action we're responding (eg, divs hiding/showing/resizing) // has had
      // a chance to complete within the DOM.
      this.$nextTick(() => {
        this.map.resize();
        if (this.$store.getters.isNavActiveTabCorridors && stateBoundaries) {
          this.zoomToCorridorState();
        } else if (parkBoundaries && (typeof parkBoundaries.geometry !== 'undefined')) {
          this.zoomToPark();
        } else {
          this.zoomMap();
        }
      });
    },

    toggleLegend() {
      if (this.legend.added) {
        this.map.removeControl(this.legend);
      }

      if (this.queryFuel && (this.queryFuel.includes('all') || this.queryFuel.length > 1)) {
        this.map.addControl(this.legend, 'bottom-right');
      }

      if (this.$store.getters.isNavActiveTabCorridors && (this.corridorRouteMode || this.$store.getters.isNavActiveTabCorridorsSearchPresent)) {
        this.map.addControl(this.legend, 'bottom-right');
      }
    },
  },
};
</script>

<style lang="scss">
#station-background,
#direction-background {
  position: absolute;
  z-index: 11;
  background-color: #000;
  opacity: .3;
  width: 100%;
  height: 100%;
}

#stations-map {
  height: 625px;
}

@import '~spinthatshit/src/loaders.scss';

#station-spinner {
  @include loader02($size: 250px, $color: #666, $border-size: 25px);
  position: absolute;
  left: 50%;
  top: 100px;
  margin-left: -125px;
}

#station-spinner-container {
  position: relative;
  margin: 0 auto;
  width: 250px;
  z-index: 13;
}

.mapboxgl-ctrl-attrib.mapboxgl-compact {
  padding-top: 0px;
  padding-bottom: 0px;
  line-height: 24px;
  height: 24px;
  background-color: transparent;
  margin-top: 0px;

  &:hover {
    padding-top: 0px;
    padding-bottom: 0px;
    background-color: #fff;
  }
}

.mapboxgl-map {
  font-family: inherit;
  line-height: normal;
}

</style>
